/**
 * Keyboard navigation utilities for dropdown/menu components
 */

export interface KeyboardNavigationItem {
  setAttribute(name: string, value: string): void
  focus(): void
  click(): void
  textContent: string | null
}

export interface KeyboardNavigationOptions {
  enabledSelector: string
  onSelectionChange?: (item: HTMLElement, index: number, total: number) => void
  onItemClick?: (item: HTMLElement) => void
}

export class KeyboardNavigationManager {
  private _cachedItems: NodeListOf<Element> | null = null
  private _cacheInvalidated = true
  private _focusedItem: HTMLElement | null = null
  private _focusedIndex = -1
  private _container: Element
  private _options: KeyboardNavigationOptions

  constructor(container: Element, options: KeyboardNavigationOptions) {
    this._container = container
    this._options = options
  }

  invalidateCache() {
    this._cachedItems = null
    this._cacheInvalidated = true
  }

  getEnabledItems(): Element[] {
    if (this._cacheInvalidated || !this._cachedItems) {
      this._cachedItems = this._container.querySelectorAll(
        this._options.enabledSelector,
      )
      this._cacheInvalidated = false
    }
    return Array.from(this._cachedItems)
  }

  setFocusedItem(index: number) {
    const items = this.getEnabledItems()

    // Clear current focus
    if (this._focusedItem) {
      this._focusedItem.setAttribute('tabindex', '-1')
      this._focusedItem.setAttribute('aria-selected', 'false')
    }

    // Normalize index (circular navigation)
    if (index < 0) {
      index = items.length - 1
    } else if (index >= items.length) {
      index = 0
    }

    // Set new focus
    this._focusedIndex = index
    this._focusedItem = items[index] as HTMLElement

    if (this._focusedItem) {
      this._focusedItem.setAttribute('tabindex', '0')
      this._focusedItem.setAttribute('aria-selected', 'true')
      this._focusedItem.focus()

      // Notify callback
      this._options.onSelectionChange?.(
        this._focusedItem,
        index + 1,
        items.length,
      )
    }
  }

  initializeFocus(lastClickedItem?: HTMLElement) {
    const items = this.getEnabledItems()

    // Set all items to tabindex -1 and role menuitem initially
    items.forEach(item => {
      const htmlItem = item as HTMLElement
      htmlItem.setAttribute('tabindex', '-1')
      htmlItem.setAttribute('role', 'menuitem')
      htmlItem.setAttribute('aria-selected', 'false')
    })

    // Find the item to focus (last clicked or first available)
    let focusIndex = 0
    if (lastClickedItem) {
      const lastClickedIndex = items.indexOf(lastClickedItem)
      if (lastClickedIndex !== -1) {
        focusIndex = lastClickedIndex
      }
    }

    if (items.length > 0) {
      this.setFocusedItem(focusIndex)
    }
  }

  handleKeyDown(event: KeyboardEvent): boolean {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.setFocusedItem(this._focusedIndex + 1)
        return true

      case 'ArrowUp':
        event.preventDefault()
        this.setFocusedItem(this._focusedIndex - 1)
        return true

      case 'Tab':
        event.preventDefault()
        // Tab moves forward, Shift+Tab moves backward
        if (event.shiftKey) {
          this.setFocusedItem(this._focusedIndex - 1)
        } else {
          this.setFocusedItem(this._focusedIndex + 1)
        }
        return true

      case 'Home':
        event.preventDefault()
        this.setFocusedItem(0)
        return true

      case 'End':
        event.preventDefault()
        this.setFocusedItem(this.getEnabledItems().length - 1)
        return true

      case 'Enter':
      case ' ':
        event.preventDefault()
        if (this._focusedItem) {
          this._focusedItem.click()
          this._options.onItemClick?.(this._focusedItem)
        }
        return true

      default:
        return false
    }
  }

  reset() {
    this._focusedItem = null
    this._focusedIndex = -1
  }

  get focusedItem() {
    return this._focusedItem
  }

  get focusedIndex() {
    return this._focusedIndex
  }
}

/**
 * Creates a live region for screen reader announcements
 */
export function createOrUpdateLiveRegion(
  container: Element,
  announcement: string,
): void {
  let liveRegion = container.querySelector('[aria-live]') as HTMLElement
  if (!liveRegion) {
    liveRegion = document.createElement('div')
    liveRegion.setAttribute('aria-live', 'polite')
    liveRegion.setAttribute('aria-atomic', 'true')
    liveRegion.style.position = 'absolute'
    liveRegion.style.left = '-10000px'
    liveRegion.style.width = '1px'
    liveRegion.style.height = '1px'
    liveRegion.style.overflow = 'hidden'
    container.appendChild(liveRegion)
  }

  liveRegion.textContent = announcement
}

/**
 * Announces current selection to screen readers
 */
export function announceSelection(
  container: Element,
  item: HTMLElement,
  position: number,
  total: number,
): void {
  const text = item.textContent?.trim() || ''
  const announcement = `${text}, ${position} of ${total}`
  createOrUpdateLiveRegion(container, announcement)
}

/**
 * Standard keyboard navigation handler for dropdown/menu components
 */
export function createKeyboardNavigationHandler(
  container: Element,
  options: KeyboardNavigationOptions,
) {
  const manager = new KeyboardNavigationManager(container, {
    ...options,
    onSelectionChange: (item, position, total) => {
      announceSelection(container, item, position, total)
      options.onSelectionChange?.(item, position, total)
    },
  })

  return {
    manager,
    handleKeyDown: (event: KeyboardEvent) => manager.handleKeyDown(event),
    invalidateCache: () => manager.invalidateCache(),
    initializeFocus: (lastClickedItem?: HTMLElement) =>
      manager.initializeFocus(lastClickedItem),
    reset: () => manager.reset(),
  }
}
